import { useEffect, useRef } from "react";

const SCROLL_DELTA = 50;
const TIME_DELTA = 200;

interface IUseExitIntentOptions {
  disable?: boolean;
}

export const useExitIntent = (onExitIntent: () => void, options?: IUseExitIntentOptions) => {
  const lastScrollY = useRef<number>(0);
  const lastScrollTime = useRef<number>(Date.now());

  useEffect(() => {
    if (options?.disable) { return; }

    // Desktop exit: mouse leave at top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        onExitIntent();
      }
    };

    // Desktop + some mobile: tab hidden
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        onExitIntent();
      }
    };

    // Mobile: fast upward scroll
    const handleScroll = () => {
      const now = Date.now();
      const currentScrollY = window.scrollY;
      const deltaY = lastScrollY.current - currentScrollY;
      const deltaTime = now - lastScrollTime.current;

      if (deltaY > SCROLL_DELTA && deltaTime < TIME_DELTA) {
        onExitIntent();
      }

      lastScrollY.current = currentScrollY;
      lastScrollTime.current = now;
    };

    document.addEventListener("mouseout", handleMouseLeave);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseout", handleMouseLeave);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [ onExitIntent, options?.disable ]);
};
